// src/components/organisms/ChallengeCommentsSection.js

import React from "react";
import PropTypes from "prop-types"; 
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const ChallengeCommentsSection = ({
  comments,
  loadingComments,
  newComment,
  setNewComment,
  handleCommentSubmit,
  snackbarOpen,
  setSnackbarOpen,
  snackbarMessage,
  theme,
}) => {
  // Handler for form submission
  const onSubmit = (e) => {
    e.preventDefault(); // Prevents the default form submission behavior
    handleCommentSubmit(); // Calls the passed-in submit handler
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="body1"
        sx={{ mb: 2, fontFamily: "basic-sans", fontWeight: "700", ml: 3 }}
      >
        Comments on this Challenge
      </Typography>

      {/* Form for adding comments */}
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          backgroundColor: "white",
          borderRadius: "50px",
          padding: "8px 16px", // Optional: Add some padding for better spacing
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px", // Simplified border radius
              "& fieldset": {
                border: "none", // Remove the outline border
              },
              "&:hover fieldset": {
                border: "none", // Remove border on hover
              },
              "&.Mui-focused fieldset": {
                border: "none", // Remove border on focus
              },
            },
          }}
        />

        <IconButton
          color="primary"
          type="submit" // Set the button type to submit
          sx={{
            backgroundColor: "#1E41FF",
            borderRadius: "50%",
            mr: 1,
            ml: 1,
            "&:hover": {
              backgroundColor: "#219A79",
            },
          }}
          aria-label="submit comment" // Added ARIA label for accessibility
        >
          <ArrowForwardIcon sx={{ color: "white" }} />
        </IconButton>
      </Box>

      {/* Display Comments */}
      {loadingComments ? (
        <CircularProgress
          sx={{
            color: theme.colors.red, 
          }}
        />
      ) : comments.length > 0 ? (
        comments.map((comment) => (
          <Box
            key={comment.id}
            sx={{ mt: 3, mb: 2 }} // Add margin below to separate each comment section
          >
            {/* Commenter's Name as a Link */}
            <Typography
              variant="body1"
              sx={{
                mb: 1, // Margin below the name to separate it from the comment box
                ml: 3,
                fontFamily: "basic-sans",
              }}
            >
              <MuiLink
                component={RouterLink}
                to={`/challenge/profile/${comment.userId}`}
                sx={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  color: "inherit", // Inherit color from Typography
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {comment.createdBy}
              </MuiLink>
            </Typography>

            {/* Comment Box */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "rgba(30, 65, 255, 0.25)",
                borderRadius: 4,
                color: "white",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontFamily: "basic-sans", lineHeight: "1.8em" }}
              >
                {comment.text}
              </Typography>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body1" sx={{ ml: 3, fontFamily: "basic-sans" }}>
          No comments yet. Be the first to comment!
        </Typography>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

// Define PropTypes for better type checking
ChallengeCommentsSection.propTypes = {
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      createdBy: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      userId: PropTypes.string.isRequired, // Ensure userId is required
    })
  ).isRequired,
  loadingComments: PropTypes.bool.isRequired,
  newComment: PropTypes.string.isRequired,
  setNewComment: PropTypes.func.isRequired,
  handleCommentSubmit: PropTypes.func.isRequired,
  snackbarOpen: PropTypes.bool.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
  snackbarMessage: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
};

export default ChallengeCommentsSection;
